import { Link, useParams } from "react-router-dom";
import { useState, useMemo } from "react";
import { ArrowRight } from "lucide-react";
import { CASE_STUDIES, INDUSTRIES } from "../data/site";

export function CaseStudiesList() {
  const [industry, setIndustry] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");

  const filtered = useMemo(() => CASE_STUDIES.filter((c) => (industry === "all" || c.industry === industry) && (regionFilter === "all" || c.region === regionFilter)), [industry, regionFilter]);

  return (
    <div data-testid="case-studies-page">
      <section className="bg-brand-blue text-white py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="eyebrow text-brand-yellow mb-6">Case studies</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">Where the work has shaped decisions.</h1>
          <p className="text-lg lg:text-xl text-white/80 max-w-3xl mt-6 leading-relaxed">A selection of engagements where senior social advice, independent review or expert evidence helped shape the decision, the approval or the community outcome that followed - across renewables, resources, infrastructure and government.</p>
        </div>
      </section>

      <section className="py-14 bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-wrap items-center gap-4">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mr-2">Filter</div>
          <select data-testid="case-industry-filter" value={industry} onChange={(e) => setIndustry(e.target.value)} className="border border-slate-300 px-4 py-2.5 text-sm bg-white">
            <option value="all">All industries</option>
            {INDUSTRIES.map((i) => <option key={i.slug} value={i.slug}>{i.title}</option>)}
          </select>
          <select data-testid="case-region-filter" value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)} className="border border-slate-300 px-4 py-2.5 text-sm bg-white">
            <option value="all">All regions</option>
            <option value="AU">🇦🇺 Australia</option>
            <option value="US">🇺🇸 United States</option>
          </select>
          <div className="ml-auto text-sm text-slate-500">{filtered.length} engagement{filtered.length !== 1 ? "s" : ""}</div>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((c) => (
            <Link key={c.slug} to={`/case-studies/${c.slug}`} data-testid={`case-card-${c.slug}`} className="block bg-white border border-slate-200 p-8 hover:border-brand-orange hover:shadow-md transition-all">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs uppercase tracking-[0.2em] text-brand-orange font-semibold">{c.outcomeTag}</span>
                <span className="text-xs text-slate-400">·</span>
                <span className="text-xs text-slate-500 uppercase tracking-widest">{c.region}</span>
              </div>
              <h3 className="font-serif text-xl text-brand-blue leading-snug mb-3 min-h-[3.5rem]">{c.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-5">{c.challenge}</p>
              <span className="text-sm font-semibold text-brand-blue inline-flex items-center gap-2">Read case study <ArrowRight className="w-4 h-4" /></span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

export function CaseStudyDetail() {
  const { slug } = useParams();
  const cs = CASE_STUDIES.find((c) => c.slug === slug);
  if (!cs) return <div className="py-32 text-center"><h1 className="font-serif text-3xl text-brand-blue">Case study not found</h1></div>;
  const industry = INDUSTRIES.find((i) => i.slug === cs.industry);

  return (
    <div data-testid={`case-detail-${slug}`}>
      <section className="bg-brand-blue text-white py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Link to="/case-studies" className="text-brand-yellow text-xs uppercase tracking-[0.2em] font-semibold hover:underline">← All case studies</Link>
          <div className="flex flex-wrap items-center gap-3 mt-6 mb-4">
            <span className="bg-brand-orange text-white px-3 py-1.5 text-xs uppercase tracking-widest font-semibold">{cs.outcomeTag}</span>
            <span className="text-xs uppercase tracking-widest text-white/60">{industry?.title}</span>
            <span className="text-xs uppercase tracking-widest text-white/60">· {cs.region === "AU" ? "🇦🇺 Australia" : "🇺🇸 United States"}</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-4xl">{cs.title}</h1>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 space-y-12">
          {cs.client && (
            <div>
              <div className="eyebrow mb-3">Client</div>
              <p className="text-slate-700 text-lg leading-relaxed">{cs.client}</p>
            </div>
          )}
          {cs.location && (
            <div>
              <div className="eyebrow mb-3">Location</div>
              <p className="text-slate-700 text-lg leading-relaxed">{cs.location}</p>
            </div>
          )}
          {[
            { label: "The challenge", body: cs.challenge },
            { label: "Our role", body: cs.role },
            { label: "Approach", body: cs.approach },
            { label: "Outcome", body: cs.outcome },
          ].map((b) => (
            <div key={b.label}>
              <div className="eyebrow mb-3">{b.label}</div>
              <p className="text-slate-700 text-lg leading-relaxed">{b.body}</p>
            </div>
          ))}
          <div className="border-t border-slate-200 pt-10">
            <Link to="/contact" className="btn-primary">Contact Us <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
